import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Library, X, Plus, Loader2 } from "lucide-react";

/**
 * Per-book series editor. Shows a dropdown of the reader's existing series with
 * a "Add to new series?" toggle that swaps in a free-text input, mirroring the
 * bulk AddToSeriesModal UX. Also supports removing a book from a series.
 *
 * Props:
 *   - open: bool
 *   - onClose: () => void
 *   - book: BookOut — the book being edited
 *   - existingSeries: string[] — distinct series names the user already has
 *   - onSaved: (updatedBook) => void
 */
export function EditSeriesModal({ open, onClose, book, existingSeries = [], onSaved }) {
  const initialName = (book?.series_name || "").trim();
  const initialIndex = book?.series_index != null ? String(book.series_index) : "";
  // Union of the user's series names + the current book's series name so
  // the current selection is always representable in the dropdown, even if
  // the book was added by someone else or the user only has this one series.
  const dropdownOptions = (() => {
    const set = new Set(existingSeries);
    if (initialName) set.add(initialName);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  })();
  const [name, setName] = useState(initialName);
  const [seriesIndex, setSeriesIndex] = useState(initialIndex);
  const [creatingNew, setCreatingNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const currentName = (book?.series_name || "").trim();
    setName(currentName);
    setSeriesIndex(book?.series_index != null ? String(book.series_index) : "");
    setSaving(false);
    setRemoving(false);
    // Only fall back to free-text when the user genuinely has no series yet
    // (no current series and no other series in their library). Otherwise the
    // dropdown is preferable.
    setCreatingNew(dropdownOptions.length === 0);
  }, [open, book?.id, dropdownOptions.length]);

  if (!open || !book) return null;

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const parsed = seriesIndex.trim() === "" ? null : Number(seriesIndex);
      const body = { series_name: trimmed };
      if (parsed != null && !Number.isNaN(parsed)) body.series_index = parsed;
      const { data } = await api.patch(`/books/${book.id}`, body);
      toast.success(`Saved to "${trimmed}"`);
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const removeFromSeries = async () => {
    if (!window.confirm(`Remove "${book.title}" from the "${initialName}" series?`)) return;
    setRemoving(true);
    try {
      // The PATCH endpoint drops fields set to null (drop_none filter) so we
      // hit the batch-clear endpoint which explicitly nulls both series fields.
      const { data } = await api.post("/books/batch-clear-series", { book_ids: [book.id] });
      toast.success(`Removed from "${initialName}"`);
      onSaved?.(data.books?.[0] || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="edit-series-modal"
    >
      <div
        className="bg-[#F2EAD3] max-w-md w-full max-h-[85vh] overflow-y-auto border border-[rgba(10,10,10,0.10)] shadow-[0_20px_60px_rgba(10,10,10,0.20)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-[rgba(10,10,10,0.08)]">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#525252] mb-2 inline-flex items-center gap-2">
              <Library className="w-3 h-3" strokeWidth={1.5} /> {initialName ? "Edit series" : "Add to series"}
            </div>
            <h3 className="font-heading text-2xl text-[#0A0A0A] tracking-tight leading-tight">{book.title}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded-sm" aria-label="Close" data-testid="edit-series-close">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label htmlFor="edit-series-name" className="block font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-2">Series name</label>
            {creatingNew || dropdownOptions.length === 0 ? (
              <input
                id="edit-series-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Mistborn"
                className="w-full bg-[#FAF5E6] border border-[rgba(10,10,10,0.18)] px-3 py-2.5 text-[#0A0A0A] font-sans focus:outline-none focus:border-[#0A0A0A]"
                data-testid="edit-series-name"
                autoFocus
              />
            ) : (
              <select
                id="edit-series-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-[#FAF5E6] border border-[rgba(10,10,10,0.18)] px-3 py-2.5 text-[#0A0A0A] font-sans focus:outline-none focus:border-[#0A0A0A]"
                data-testid="edit-series-name"
              >
                <option value="">— Pick a series —</option>
                {dropdownOptions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            )}
            {dropdownOptions.length > 0 && (
              <button
                type="button"
                onClick={() => { setCreatingNew((v) => !v); setName(""); }}
                className="mt-3 inline-flex items-center gap-1.5 font-mono uppercase tracking-widest text-[10px] text-[#0A0A0A] hover:underline"
                data-testid="edit-series-toggle-new"
              >
                <Plus className="w-3 h-3" strokeWidth={1.5} />
                {creatingNew ? "Pick from existing series instead" : "Add to new series?"}
              </button>
            )}
          </div>

          <div>
            <label htmlFor="edit-series-index" className="block font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-2">
              Book number <span className="normal-case tracking-normal text-[#8C7F65]">(optional)</span>
            </label>
            <input
              id="edit-series-index"
              type="number"
              step="any"
              value={seriesIndex}
              onChange={(e) => setSeriesIndex(e.target.value)}
              placeholder="e.g. 3"
              className="w-32 bg-[#FAF5E6] border border-[rgba(10,10,10,0.18)] px-3 py-2.5 text-[#0A0A0A] font-sans focus:outline-none focus:border-[#0A0A0A]"
              data-testid="edit-series-index"
            />
          </div>

          <div className="flex items-center justify-between gap-3 pt-4 border-t border-[rgba(10,10,10,0.08)] flex-wrap">
            {initialName ? (
              <button
                type="button"
                onClick={removeFromSeries}
                disabled={saving || removing}
                className="font-mono uppercase tracking-widest text-[10px] text-[#8A0F0F] hover:text-[#5C0808] underline underline-offset-4 disabled:opacity-50"
                data-testid="edit-series-remove"
              >
                {removing ? "Removing…" : "Remove from series"}
              </button>
            ) : <span />}
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="font-mono uppercase tracking-widest text-[10px] text-[#525252] hover:text-[#0A0A0A] px-3 py-2"
                data-testid="edit-series-cancel"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={saving || removing || !name.trim()}
                className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm transition-colors disabled:opacity-50"
                data-testid="edit-series-submit"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : <Library className="w-3.5 h-3.5" strokeWidth={1.5} />}
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
